import { supabase } from "./supabase";
import * as db from "./db";
import * as lager from "./dokumentlager";
import { finnesAlt } from "@infoflyt/shared/lager";

/**
 * Alt modulen trenger aa vite om hvem som leverer.
 *
 * Ikke hele `Session`: rapporten hoerer til en produksjon, og et mindre
 * krav er lettere aa oppfylle fra en flate som bare har den. Medlemmet stod
 * her til 02.11.2026 — naa setter `lever_rapport` forfatteren selv, og en
 * id appen ikke bruker er en invitasjon til aa tro at den bestemmer.
 */
export interface Avsender {
  readonly produksjonId: string;
}

/**
 * Rapporten opp til produksjonen.
 *
 * `docs/05-modul-logg.md` beskrev v1: PDF-en lages lokalt og deles gjennom
 * systemets delingsark, og «mottaker velges av regi». Det var en akseptert
 * midlertidighet fordi det ikke fantes noe sted aa levere til. Naa finnes
 * det — rapporten hoerer til produksjonen, og redaktoeren finner den paa
 * nettflata. Avgjort 22.10.2026.
 *
 * PRIVAT GAAR INGEN VEI HERFRA. En privat logg hoerer ingen produksjon til;
 * den sendes av brukeren selv. Se AvsluttPane.
 *
 * LOKALT FOERST, OGSAA HER. Dagen avsluttes ofte uten dekning. Rapporten
 * legges i koe, PDF-en blir liggende i OPFS, og begge deler gaar bort naar
 * den er levert. Fila slettes samme oeyeblikk — en rapport er hele dagens
 * logg med navngitte deltakere, og den skal ikke bli liggende i klartekst
 * lenger enn den maa.
 *
 * VI TAR VARE PAA FILA, IKKE OPPSKRIFTEN. Det er DEN som ble levert. En ny
 * generering naar dekningen kommer kunne gitt et annet dokument, om loggen
 * var rettet i mellomtiden — og da ville versjonsnummeret loeyet.
 */

/**
 * Det som ikke gikk opp sier fra.
 *
 * Samme moenster som `loggsynk`: en levering som feiler i stillhet er det
 * verste utfallet — regi faar «Rapporten er levert», og redaktoeren har
 * ingenting.
 */
function klag(hva: string, feil: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[infoflyt] rapportsynk: ${hva}`, feil);
  const kode = typeof feil === "object" && feil !== null && "code" in feil
    ? String((feil as { code: unknown }).code) : "";
  const sagt = typeof feil === "object" && feil !== null && "message" in feil
    ? String((feil as { message: unknown }).message) : String(feil);
  const ord = kode === "" ? `${hva}: ${sagt}` : `${hva} ${kode}: ${sagt}`;
  /* Byggmerket staar PAA klagen. Uten det er det foerste spoersmaalet alltid
     «hvilken versjon kjoerer telefonen», og det er et sloesende spoersmaal. */
  sisteKlage = `${ord} · bygd ${__BYGG__}`;
}

/**
 * Siste avvisning, i klartekst.
 *
 * `console.error` finnes ikke paa en telefon. En levering som ble avvist
 * endte derfor som «venter paa nett» paa skjermen og ingenting annet noe
 * sted, og den eneste veien til aa finne ut hva som gikk galt var aa
 * gjenskape det paa en maskin med utviklerverktoey. Naa kan flata si det.
 */
let sisteKlage = "";

export function klagen(): string {
  return sisteKlage;
}

/**
 * HVORFOR rapporten ikke gikk opp.
 *
 * Flata gjettet foer: den sa «venter paa nett» uansett grunn, ogsaa naar
 * telefonen hadde full dekning og tjeneren hadde avvist raden. Et galt svar
 * er verre enn ingen, fordi regi staar der og venter paa en linje som
 * allerede er der.
 *
 * - `uten-nett`     ingen dekning, eller appen kjoerer uten tjener
 * - `venter-paa-loggen` oekten er ikke oppe ennaa. Gaar av seg selv
 * - `avvist`        tjeneren sa nei. `klagen()` har ordene
 * - `fila-borte`    PDF-en finnes ikke lenger lokalt. Maa lages paa nytt
 */
export type Grunn = "uten-nett" | "venter-paa-loggen" | "avvist" | "fila-borte";

/** Stien i boetta. Produksjonen foerst, saa oekten — som dokumentene. */
function sti(r: db.Rapportkoe, versjon: number): string {
  return `${r.productionId}/${r.sessionId}/v${versjon}.pdf`;
}

/**
 * VERSJONEN AVGJOERES VED SENDING, MOT TJENEREN.
 *
 * Her laa feilen. Nummeret ble satt da rapporten gikk i koe, og hentet fra
 * koen selv — som toemmes naar en rapport er levert. Andre levering for
 * samme logg fikk derfor `v1` paa nytt, kolliderte med
 * `unique (session_id, version)`, og ble staaende i koen og blokkere alt
 * bak seg. Regi fikk «venter paa nett» for alltid, og redaktoeren fikk
 * ingenting.
 *
 * «Hvilken levering i rekka er dette» er et spoersmaal bare tjeneren kan
 * svare paa. Uten nett faller vi tilbake paa det koen vet, og da gaar det
 * bra uansett: en kollisjon haandteres under.
 */
async function nesteVersjon(sessionId: string, fallback: number): Promise<number> {
  if (supabase === null) return fallback;
  const { data, error } = await supabase
    .from("report")
    .select("version")
    .eq("session_id", sessionId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error !== null) return fallback;
  return data === null ? 1 : Number(data.version) + 1;
}

/**
 * Finnes oekten i basen?
 *
 * Billig: ett oppslag paa primaernoekkelen, og bare for oekter som har en
 * rapport i koen. Svaret caches ikke — en oekt som ikke var oppe i forrige
 * takt kan vaere oppe i den neste, og det er hele poenget.
 */
async function oektFinnes(sessionId: string): Promise<boolean> {
  if (supabase === null) return false;
  const { data, error } = await supabase
    .from("log_session").select("id").eq("id", sessionId).maybeSingle();
  if (error !== null) return false;
  return data !== null;
}

export interface Utfall {
  /** Sann naar rapporten faktisk kom fram. Ellers ligger den i koe. */
  readonly levert: boolean;
  /**
   * Nummeret den fikk, eller null naar den venter.
   *
   * Det avgjoeres av tjeneren og ikke av telefonen — se `nesteVersjon`. Og
   * derfor kan ikke flata love et nummer foer leveringen er gjort.
   */
  readonly versjon: number | null;
  /** Hvorfor den venter. `null` naar den kom fram. */
  readonly grunn: Grunn | null;
  /**
   * Om PDF-en kom ned paa telefonen.
   *
   * Er den false, finnes rapporten bare i minnet: gaar den ikke opp foer
   * appen avsluttes, er den borte, og dagen maa avsluttes paa nytt. Det er
   * ikke noe appen kan rette, men det er noe regi maa vite.
   */
  readonly lokalt: boolean;
}

/**
 * Legger rapporten i koe og proever aa sende med én gang.
 *
 * Venter den paa nett, er dagen likevel avsluttet. Begge deler er et
 * gyldig utfall.
 */
export async function lever(
  avsender: Avsender,
  felt: {
    readonly sessionId: string;
    readonly pdf: Blob;
    readonly meta: Record<string, unknown>;
    readonly oppsummering: string;
  },
): Promise<Utfall> {
  const produksjonId = avsender.produksjonId;
  if (produksjonId === "" || produksjonId === "privat") {
    return { levert: false, versjon: null, grunn: null, lokalt: true };
  }

  await db.openDb(produksjonId);
  const versjon = await db.nesteRapportversjon(felt.sessionId);
  const id = db.newId();
  const filnavn = `rapport-${id}.pdf`;

  /*
   * Fila foerst, og i minnet uansett.
   *
   * Gikk skrivingen galt, er minnet det eneste stedet rapporten finnes —
   * og da er det bedre aa sende den derfra enn aa la dagen ende uten
   * rapport. Koeraden peker fortsatt paa filnavnet, slik at en app som
   * startes paa nytt proever OPFS.
   */
  iMinnet.set(id, felt.pdf);
  const lokalt = await lager.lagre(filnavn, felt.pdf);
  await db.koeRapport({
    id,
    productionId: produksjonId,
    sessionId: felt.sessionId,
    versjon,
    filnavn,
    meta: JSON.stringify(felt.meta),
    oppsummering: felt.oppsummering,
    lukketUtcMs: Date.now(),
  });

  /*
   * LOGGEN FOERST, SAA RAPPORTEN.
   *
   * Rapporten henger paa oekten, og oekten gaar opp med loggkoen. Nudgen
   * setter den takten i gang naa, slik at de to ikke kappes om hvem som
   * kommer foerst. Se App.tsx: `infoflyt:loggkoe` toemmer loggen foer
   * rapporten.
   */
  window.dispatchEvent(new CustomEvent("infoflyt:loggkoe"));

  /*
   * ET PAR FORSOEK FOER FLATA SIER «VENTER».
   *
   * Dagen avsluttes rett etter siste loggpost, og da kan oekten ligge i
   * loggkoen i noen hundre millisekunder. «Rapporten venter paa nett» om noe
   * som gaar opp to sekunder senere er en loegn regi ikke kan gjoere noe
   * med — og den gamle flata ble staaende paa den for alltid.
   */
  for (let forsoek = 0; forsoek < 4; forsoek += 1) {
    await send(avsender);
    const versjonen = nummer.get(id);
    if (versjonen !== undefined) {
      nummer.delete(id);
      grunner.delete(id);
      return { levert: true, versjon: versjonen, grunn: null, lokalt };
    }
    if (forsoek < 3) await new Promise((r) => { window.setTimeout(r, 700); });
  }
  const grunn = grunner.get(id) ?? (navigator.onLine ? "avvist" : "uten-nett");
  grunner.delete(id);
  return { levert: false, versjon: null, grunn, lokalt };
}

/**
 * Nummeret hver levert rapport fikk, etter koe-id.
 *
 * `lever` kan ikke regne det ut selv — tjeneren avgjoer det — og rapporten
 * kan gaa opp i en annen `send` enn den `lever` selv startet. Derfor et kart
 * og ikke «siste»: det er DENNE rapportens nummer flata skal vise.
 */
const nummer = new Map<string, number>();

/** Hvorfor hver rapport i koen ikke gikk opp, etter koe-id. */
const grunner = new Map<string, Grunn>();

/** Oekter vi alt har bedt loggkoen om. Hindrer at de to kaller i ring. */
const bedtOm = new Set<string>();

/**
 * PDF-en i minnet, etter koe-id, saa lenge appen lever.
 *
 * LAGRINGEN KAN LYVE. Paa iOS skrev OPFS en fil som fanns og var tom, og
 * rapporten gikk opp som null byte — tjeneren svarte «No content provided»,
 * og redaktoeren fikk ingenting. Lagringen er rettet, men den er ikke det
 * eneste stedet rapporten finnes lenger: dagen avsluttes ÉN gang, og en
 * rapport skal ikke kunne gaa tapt fordi en telefon ikke fikk skrevet en
 * fil. Minnet gjelder bare denne oppstarten — overlever ikke appen, er fila
 * i OPFS det som er igjen.
 */
const iMinnet = new Map<string, Blob>();

/**
 * Toemmer koen. Returnerer hvor mange rapporter som gikk opp.
 *
 * REKKEFOELGEN ER FILA FOERST, RADEN ETTERPAA, som dokumentopplastingen paa
 * nettflata: feiler opplastingen, finnes det ingen rad som peker paa en fil
 * som ikke er der — og redaktoeren ser aldri en rapport hen ikke kan aapne.
 */
export async function send(avsender: Avsender): Promise<number> {
  if (supabase === null || !navigator.onLine) {
    /* Hele koen venter paa det samme, og flata skal kunne si hvilket. */
    await merkAlle(avsender, "uten-nett");
    return 0;
  }
  const produksjonId = avsender.produksjonId;
  if (produksjonId === "" || produksjonId === "privat") return 0;

  await db.openDb(produksjonId);
  const koe = await db.rapportkoen(produksjonId);
  let sendt = 0;

  /*
   * ========  FORFATTEREN SETTES AV SERVEREN  ========
   *
   * Det stod en oppslagsrunde her: appen spurte `min_medlemsrad` rett foer
   * sendingen, fordi `report`-policyen krever
   * `author_id = tilgang.mitt_medlem(...)` og appen satte `author_id` selv.
   * Verdiene stemte, og raden ble avvist likevel — og «42501» sier ikke
   * hvilken av de to den er uenig om.
   *
   * `lever_rapport` regner den ut paa innsiden. Da finnes det bare én
   * verdi, de to kan ikke komme i utakt, og appen har ingenting aa slaa
   * opp: ingen klient skriver en forfatter her.

  /*
   * Oekter som ikke gaar naa. Resten av koen skal likevel toemmes.
   *
   * HER LAA DEN ANDRE HALVDELEN AV FEILEN. Loekka stoppet ved den foerste
   * rapporten som ikke gikk, og en rapport som ALDRI kom til aa gaa — fordi
   * oekten ikke var oppe — ble staaende og blokkere alt bak seg. Regi fikk
   * «venter paa nett» for en rapport som laa fint i koen, og redaktoeren
   * fikk ingenting. Rekkefoelgen betyr bare noe INNENFOR én oekt, der v1
   * skal opp foer v2, og det er nettopp det `hopp` tar vare paa.
   */
  const hopp = new Set<string>();

  for (const r of koe) {
    if (hopp.has(r.sessionId)) continue;

    /*
     * Minnet foerst, OPFS etterpaa, og en tom fil regnes som ingen fil.
     *
     * En null-byte opplasting kommer tilbake som «No content provided» —
     * og det var nettopp det som skjedde: fila fanns, den var tom, og
     * ingen av leddene foer tjeneren sa fra.
     */
    const kandidat = iMinnet.get(r.id) ?? await lager.lesFil(r.filnavn);
    const fil = kandidat !== undefined && kandidat !== null && kandidat.size > 0
      ? kandidat : null;
    if (fil === null) {
      /*
       * Fila er borte, og uten den finnes det ingen rapport aa levere.
       * Raden ryddes, ellers blir den liggende og proeve for alltid — men
       * det skal ikke skje i stillhet: rapporten er TAPT, og regi maa lage
       * den paa nytt. Flata sier det.
       */
      grunner.set(r.id, "fila-borte");
      iMinnet.delete(r.id);
      await db.rapportkoeKvitter(r.id);
      continue;
    }

    /*
     * OEKTEN MAA VAERE OPPE FOERST.
     *
     * `report.session_id` peker paa `log_session`, og den raden kommer opp
     * med loggkoen — en annen koe, med sin egen takt. Avsluttes dagen i det
     * dekningen kommer tilbake, kan rapporten ligge klar foer oekten er
     * oppe, og da avviser Postgres hele raden.
     *
     * Dette er ikke en feil, det er et «ennaa ikke»: loggen gaar opp av seg
     * selv, og rapporten foelger ved neste takt. Derfor ingen klage.
     */
    if (!(await oektFinnes(r.sessionId))) {
      /*
       * BER OM OEKTEN, VENTER IKKE PAA DEN.
       *
       * Loggkoen kvitterer én gang. Er oekten alt kvittert, men raden
       * likevel ikke i basen — svaret kom aldri fram, eller den ble ryddet
       * bort paa tjeneren — ville rapporten ventet for alltid paa noe ingen
       * kom til aa sende. Naa legges oekten i koe paa nytt, og loggen tar
       * den ved neste takt.
       */
      if (!bedtOm.has(r.sessionId)) {
        bedtOm.add(r.sessionId);
        await db.koeOektPaaNytt(r.sessionId);
        /* Og saa gaar loggen NAA, ikke ved neste takt. Én gang per oekt:
           nudgen kaller hit igjen, og uten vakten ville de to kalt paa
           hverandre i ring. */
        window.dispatchEvent(new CustomEvent("infoflyt:loggkoe"));
      }
      grunner.set(r.id, "venter-paa-loggen");
      hopp.add(r.sessionId);
      continue;
    }

    let versjon = await nesteVersjon(r.sessionId, r.versjon);
    let levert = false;

    /*
     * TRE FORSOEK, MED NYTT NUMMER HVER GANG.
     *
     * To telefoner kan levere for samme oekt i samme sekund, og da taper
     * den ene kappleopet om nummeret. Et nytt forsoek med neste nummer er
     * riktigere enn aa gi opp — og viktigere: en kollisjon maa ALDRI kunne
     * kile koen fast, slik den gjorde.
     */
    for (let forsoek = 0; forsoek < 3 && !levert; forsoek += 1) {
      const bane = sti(r, versjon);
      /*
       * RAA BYTES, IKKE BLOB. HER LAA FEILEN.
       *
       * `storage.upload` med en Blob bygger en FormData og legger fila i et
       * felt UTEN NAVN: `body.append("", fil)`. Paa iOS kom den delen ikke
       * med, og tjeneren svarte «No content provided» paa en opplasting som
       * hadde en helt gyldig PDF i haanden. En ArrayBuffer tar den andre
       * veien i klienten — raa kropp med `content-type` — og der finnes det
       * ingen multipart som kan miste noe.
       */
      const kropp = await fil.arrayBuffer();
      if (kropp.byteLength === 0) {
        grunner.set(r.id, "fila-borte");
        break;
      }
      /*
       * `upsert: false`. EN LEVERT RAPPORT OVERSKRIVES ALDRI.
       *
       * Stien baerer versjonen, saa en ny versjon legger seg ved siden av
       * den gamle. Med `upsert: true` kunne den samme stien likevel faa
       * nytt innhold — og da ville v1 byttet innhold uten at noen rad
       * endret seg. Policyen tillater det ikke lenger heller; se
       * 20261029130000_rapporter_og_dokumenter_overskrives_ikke.sql.
       *
       * Moeter vi vaar egen forrige opplasting, er den levert: svaret kan
       * ha gaatt tapt paa vei tilbake uten at fila gjorde det. Da gaar vi
       * videre til raden i stedet for aa staa fast for alltid.
       */
      const { error: filfeil } = await supabase.storage
        .from("rapporter")
        .upload(bane, kropp, { contentType: "application/pdf", upsert: false });
      if (filfeil !== null && !finnesAlt(filfeil)) {
        klag("opplasting", filfeil); grunner.set(r.id, "avvist"); break;
      }

      /*
       * ========  SERVEREN SETTER FORFATTEREN  ========
       *
       * Her sto en `upsert` med `author_id` fra appen. Policyen krever
       * `author_id = tilgang.mitt_medlem(...)`, saa hvert forsoek var en
       * paastand serveren kunne vaere uenig i — og «42501» sier ikke
       * hvilken av de to verdiene den er uenig om. Fire runder med
       * gjetting kostet en kveld.
       *
       * `lever_rapport` regner den ut paa innsiden. De to kan da ikke
       * komme i utakt, fordi det bare finnes én verdi. Og den svarer paa
       * norsk naar den ikke kan: ikke medlem, ukjent oekt, oekt i en
       * annen produksjon. Se migrasjon 20261102200000.
       *
       * VERSJONEN KOMMER TILBAKE. Serveren vet hvilke numre som er tatt,
       * og `unique (session_id, version)` haandteres der inne.
       */
      const { data: nummeret, error } = await supabase.rpc("lever_rapport", {
        p_id: r.id,
        p_oekt: r.sessionId,
        p_produksjon: r.productionId,
        /* Blokkmodellen fra skjemaet. I v1 finnes bare `paragraph`, fordi
           appen bare har en fritekstoppsummering. Se docs/03-datamodell.md. */
        p_blocks: r.oppsummering === ""
          ? [] : [{ type: "paragraph", text: r.oppsummering }],
        p_meta: JSON.parse(r.meta) as Record<string, unknown>,
        p_pdf_sti: bane,
        p_lukket: new Date(r.lukketUtcMs).toISOString(),
      });

      if (error === null) {
        if (typeof nummeret === "number") versjon = nummeret;
        levert = true;
        break;
      }
      /*
       * HVA DEN PROEVDE MED, IKKE BARE AT DEN FEILET.
       *
       * `lever_rapport` svarer paa norsk naar den ikke kan — ikke medlem,
       * ukjent oekt, oekt i en annen produksjon — saa teksten fra serveren
       * staar som den er. Produksjonen og oekten foelger med, fordi koen
       * kan ha flere oekter og klagen ellers ikke sier hvilken.
       */
      klag(
        `rapporten (produksjon ${r.productionId.slice(0, 8)}, `
        + `oekt ${r.sessionId.slice(0, 8)})`,
        error,
      );
      grunner.set(r.id, "avvist");
      break;
    }

    if (!levert) { hopp.add(r.sessionId); continue; }
    nummer.set(r.id, versjon);

    /* Levert. Da skal ikke dagens logg ligge i klartekst paa telefonen —
       hverken i OPFS eller i minnet. */
    iMinnet.delete(r.id);
    await lager.slett(r.filnavn);
    await db.rapportkoeKvitter(r.id);
    sendt += 1;
  }
  return sendt;
}

/** Produksjonen det kan leveres til, eller null for privat og tomt. */
function maal(avsender: Avsender): string | null {
  const id = avsender.produksjonId;
  return id === "" || id === "privat" ? null : id;
}

/** Samme grunn paa alt som staar i koen. Brukt naar linja er borte. */
async function merkAlle(avsender: Avsender, grunn: Grunn): Promise<void> {
  const id = maal(avsender);
  if (id === null) return;
  await db.openDb(id);
  for (const r of await db.rapportkoen(id)) grunner.set(r.id, grunn);
}

/**
 * Hvilket nummer den siste leverte rapporten for oekten fikk.
 *
 * Flata trenger det naar rapporten gikk opp i bakgrunnen og ikke i den
 * `lever` som tegnet skjermen: da staar det «venter» paa en rapport som er
 * kommet fram, og skjermen skal rette seg selv.
 */
export async function levertVersjon(sessionId: string): Promise<number | null> {
  const siste = await nesteVersjon(sessionId, 0);
  return siste <= 1 ? null : siste - 1;
}

/** Hvor mange rapporter som venter paa nett. Flata viser det. */
export async function ventende(produksjonId: string): Promise<number> {
  if (produksjonId === "" || produksjonId === "privat") return 0;
  await db.openDb(produksjonId);
  return (await db.rapportkoen(produksjonId)).length;
}
